import { Injectable } from '@angular/core';
import * as uuid from 'uuid';
import { Beneficiary } from '../interfaces/policy-admin-interfaces/beneficiary.interface';
import { PartyPerson } from '../interfaces/policy-admin-interfaces/party-person.interface';
import { Person } from '../interfaces/policy-admin-interfaces/person.interface';
import { Entity } from '../interfaces/policy-admin-interfaces/entity.interface';
import { Party } from '../interfaces/policy-admin-interfaces/party.interface';
import { BeneChild } from '../interfaces/policy-admin-interfaces/bene-child.interface';
import { AmorphicService } from '../../../../../coverpath-common/angular/services/amorphic/amorphic.service';
import { from } from 'rxjs';
import { map } from 'rxjs/operators';
import { Product } from '../../../../js/static/Product';
import { BulkUpdateBeneficiary } from '../interfaces/policy-admin-interfaces/bulk-update-beneficiary.interface';
import { Utils } from '../../../Utils';
import { Address } from '../interfaces/policy-admin-interfaces/address.interface';

@Injectable()
export class BeneListService {
  private static primaryBeneList: Array<Beneficiary> = [];
  private static contingentBeneList: Array<Beneficiary> = [];
  private static deletedPrimaryBeneList: Array<Beneficiary> = [];
  private static deletedContingentBeneList: Array<Beneficiary> = [];
  private static currentBene: Beneficiary;
  private static type: string;
  private static policyId: string;

  private static relationIdToIndexMap: { [key: string]: number } = {};

  private static init: boolean = true;
  private static splitBene: boolean = true;

  private static custodian: any = null;
  private static changes: boolean = false;
  private static trustEstateCount: number = 0;

  private static governmentId: string;

  static custodianChanged: boolean = false;
  static primaryChanged: boolean = false;
  static contingentChanged: boolean = false;

  private policyAdminDown: boolean = false;
  private readonly channel: {};
  private readonly channelName: string;
  private showComplexOrTertiaryBeneMessage: boolean = false;

  static ownerResidentialState: string = '';
  controller;

  static useSecondary: boolean;

  constructor(private amorphicService: AmorphicService) {
    this.controller = amorphicService.controller;
    this.channel = Product.channels[amorphicService.controller.customer.application.primaryPolicy.getChannelCode()];
    this.channelName =
      Product.channels[amorphicService.controller.customer.application.primaryPolicy.getChannelCode()].name;
  }

  getBeneList(governmentId: string, useSecondary?: boolean) {
    BeneListService.governmentId = governmentId;
    BeneListService.useSecondary = useSecondary;
    const subscription = from(this.controller.getBeneficiaryInfo(useSecondary));
    return subscription.pipe(
      map((res) => {
        this.parseResponses(res);
        this.resetChanges();
      })
    );
  }

  parseResponses(response) {
    if (!response) {
      this.policyAdminDown = true;
    } else if (this.hasTertiaryOrComplexBeneficiaries(response)) {
      this.showComplexOrTertiaryBeneMessage = true;
    } else {
      this.policyAdminDown = false;

      const policy = response.policy;
      BeneListService.ownerResidentialState = policy.ownerResidentialState;

      if (policy.custodian && BeneListService.custodian === null) {
        let state = '';
        if (policy.custodian.contactInfo && policy.custodian.contactInfo.residentialAddress) {
          state = policy.custodian.contactInfo.residentialAddress.state;
        }
        BeneListService.custodian = {
          firstName: policy.custodian.party.firstName,
          lastName: policy.custodian.party.lastName,
          state: state,
        };
      }

      BeneListService.policyId = policy.policyId;
      BeneListService.splitBene = false;
      if (BeneListService.init) {
        BeneListService.init = false;
        this.makeBeneList(policy.primaryBeneficiary, 'Primary');
        this.makeBeneList(policy.contingentBeneficiary, 'Contingent');
      }
      BeneListService.splitBene = true;
    }
  }

  hasTertiaryOrComplexBeneficiaries(response): boolean {
    const policy = response.policy;
    if (!!policy.tertiaryBeneficiary && policy.tertiaryBeneficiary.length > 0) {
      return true;
    }

    const primaryBeneficiaryList = policy.primaryBeneficiary;
    for (let i = 0; i < primaryBeneficiaryList.length; i++) {
      const primaryBeneficiary = primaryBeneficiaryList[i];
      if (primaryBeneficiary.beneficiaryType === '35') {
        return true;
      }
    }

    const contingentBeneficiaryList = policy.contingentBeneficiary;
    for (let i = 0; i < contingentBeneficiaryList.length; i++) {
      const contingentBeneficiary = contingentBeneficiaryList[i];
      if (contingentBeneficiary.beneficiaryType === '35') {
        return true;
      }
    }

    return false;
  }

  makeBeneList(beneList, type: string): void {
    const d = new Date();
    const actionDate = d.getMonth() + 1 + '\\' + d.getDate() + '\\' + d.getFullYear();

    for (let i = 0; i < beneList.length; i++) {
      const beneIn = beneList[i];

      let bene: Beneficiary;
      bene = {
        ratio: beneIn.ratio * 100,
        action: 'modified',
        actionDate: actionDate,
        beneficiaryType: beneIn.beneficiaryType,
        id: beneIn._id,
      };

      if (beneIn.party) {
        bene.partyInfo = {};
        bene.partyInfo.phone = {
          number: beneIn.contactInfo.phone ? beneIn.contactInfo.phone.number : '',
        };
        bene.partyInfo.email = beneIn.contactInfo.email;
        bene.partyInfo.name = beneIn.party.fullName;
      } else if (beneIn.relatedParty) {
        bene.relatedParty = {
          partyInfo: {
            person: {
              firstName: beneIn.relatedParty.firstName,
              middleName: beneIn.relatedParty.middleName ? beneIn.relatedParty.middleName : '',
              lastName: beneIn.relatedParty.lastName,
            },
          },
        };
      }
      const childTypeList = [
        '11',
        '11-1',
        '11-2',
        '12',
        '12-1',
        '12-2',
        '13',
        '13-1',
        '13-2',
        '14',
        '14-1',
        '14-2',
        '15',
        '15-1',
        '15-2',
      ];
      if (childTypeList.indexOf(beneIn.beneficiaryType) !== -1) {
        bene.beneficiaryPartyType = 'children';
        bene.dependentBenes = this.getChildren(beneIn);
      }

      if (beneIn.beneficiaryType === '24' || beneIn.beneficiaryType === '6' || beneIn.beneficiaryType === '7') {
        const formedDate = this.dateFormat(beneIn.party.formedDate) || '';
        const temp: Entity = {
          trust: true,
          trustType: beneIn.party.trustType,
          formedDate: formedDate,
          govtId: beneIn.party.govtId || '',
        };
        temp.legalAddress = {
          street: beneIn.contactInfo.legalAddress ? beneIn.contactInfo.legalAddress.street : '',
          line1: beneIn.contactInfo.legalAddress ? beneIn.contactInfo.legalAddress.line1 : '',
          line2: beneIn.contactInfo.legalAddress ? beneIn.contactInfo.legalAddress.line2 : '',
          city: beneIn.contactInfo.legalAddress ? beneIn.contactInfo.legalAddress.city : '',
          state: beneIn.contactInfo.legalAddress ? beneIn.contactInfo.legalAddress.state : '',
          zip: beneIn.contactInfo.legalAddress ? beneIn.contactInfo.legalAddress.zip : '',
        };

        if (beneIn.party.govtIdType) {
          temp.govtIdType = beneIn.party.govtIdType;
        }
        (bene.partyInfo as Party).entity = temp;
      }
      if (beneIn.beneficiaryType === '2') {
        const formedDate = this.dateFormat(beneIn.party.formedDate) || '';
        const temp: Entity = {
          estate: true,
          formedDate: formedDate,
          govtId: beneIn.party.govtId || '',
        };
        if (beneIn.party.govtIdType) {
          temp.govtIdType = beneIn.party.govtIdType;
        }
        (bene.partyInfo as Party).entity = temp;
      }

      const personTypeList = ['1', '1-1', '1-2', '9', '9-1', '9-2'];
      if (personTypeList.indexOf(bene.beneficiaryType) !== -1) {
        const dob = this.dateFormat(beneIn.party.dob) || '';
        const temp: Person = {
          firstName: beneIn.party.firstName,
          middleName: beneIn.party.middleName ? beneIn.party.middleName : '',
          lastName: beneIn.party.lastName,
          dob: dob,
          govtId: beneIn.party.govtId || '',
          govtIdType: 'ssn',
        };

        (bene.partyInfo as PartyPerson).person = temp;

        bene.partyInfo.person.residentialAddress = {
          street: beneIn.contactInfo.residentialAddress ? beneIn.contactInfo.residentialAddress.street : '',
          line1: beneIn.contactInfo.residentialAddress ? beneIn.contactInfo.residentialAddress.line1 : '',
          line2: beneIn.contactInfo.residentialAddress ? beneIn.contactInfo.residentialAddress.line2 : '',
          city: beneIn.contactInfo.residentialAddress ? beneIn.contactInfo.residentialAddress.city : '',
          state: beneIn.contactInfo.residentialAddress ? beneIn.contactInfo.residentialAddress.state : '',
          zip: beneIn.contactInfo.residentialAddress ? beneIn.contactInfo.residentialAddress.zip : '',
        };
        if (beneIn.party.relations[0]) {
          bene.partyInfo.relation = {
            description: beneIn.party.relations[0].description,
            //govtId (ssn) of the insured, not of the bene
            govtId: BeneListService.governmentId,
            govtIdType: 'ssn',
          };
        }
        bene.beneficiaryPartyType = 'person';
      }
      if (beneIn.beneficiaryType === '7') {
        bene.beneficiaryPartyType = 'other';
      }
      this.addBeneList(bene, type);
    }
  }

  dateFormat(dateIn: string): string {
    if (!dateIn) {
      return '';
    }

    const splitDateString = dateIn.split('T')[0]; // ['1995-12-17', 03:24:00] grabs just the date
    const splitToFormatDateString = splitDateString.split('-');

    const month = splitToFormatDateString[1];
    const day = splitToFormatDateString[2];
    const year = splitToFormatDateString[0];
    return month + '/' + day + '/' + year;
  }

  savePrimaryAndContingentBenes() {
    for (let i = 0; i < this.getPrimaryBeneList().length; i++) {
      this.getPrimaryBeneList()[i].ratio /= 100;
    }
    for (let i = 0; i < this.getContingentBeneList().length; i++) {
      this.getContingentBeneList()[i].ratio /= 100;
    }
    const body = {
      context: {
        requestId: uuid.v1(),
        userId: 'Owner',
      },
      payload: {
        policyId: BeneListService.policyId,
        updateInformation: {
          primary: this.mapToBulkUpdateBeneficiary(this.getPrimaryBeneList()),
          secondary: this.mapToBulkUpdateBeneficiary(this.getContingentBeneList()),
          tertiary: [],
        },
      },
    };

    const subscription = from(
      this.controller.updatePolicyInfoInPolicyAdmin(
        'policy-admin/bulk-update-beneficiaries',
        body,
        BeneListService.useSecondary
      )
    );
    return subscription.pipe(
      map(() => {
        for (let i = 0; i < this.getPrimaryBeneList().length; i++) {
          this.getPrimaryBeneList()[i].ratio *= 100;
        }
        for (let i = 0; i < this.getContingentBeneList().length; i++) {
          this.getContingentBeneList()[i].ratio *= 100;
        }
      })
    );
  }

  saveCustodian() {
    let partyInfo = null;
    if (BeneListService.custodian) {
      partyInfo = {
        person: {
          firstName: BeneListService.custodian.firstName,
          lastName: BeneListService.custodian.lastName,
          residentialAddress: {
            state: BeneListService.custodian.state,
          },
        },
      };
    }

    const body = {
      context: {
        requestId: uuid.v1(),
        userId: 'Owner',
      },
      payload: {
        policyId: BeneListService.policyId,
        custodian: {
          partyInfo: partyInfo,
        },
      },
    };

    const subscription = from(
      this.controller.updatePolicyInfoInPolicyAdmin('policy-admin/update-custodian', body, BeneListService.useSecondary)
    );
    return subscription.pipe(
      map((res) => {
        //tslint:disable-next-line
        console.log(res);
      })
    );
  }

  private assessRelations(beneficiary): void {
    if (beneficiary.relatedParty) {
      const listOfRelations = beneficiary.relatedParty.relations;

      for (let i: number = 0; i < listOfRelations.length; i++) {
        if (listOfRelations[i].target) {
          const relationId = listOfRelations[i].target._id;
          BeneListService.relationIdToIndexMap[relationId] = i;
        }
      }
    }
  }

  getChildren(beneIn): BeneChild[] {
    this.assessRelations(beneIn);

    const toReturn: BeneChild[] = [];
    for (let i = 0; i < beneIn.dependentBenes.length; i++) {
      let curr = beneIn.dependentBenes[i];

      const ratio = curr.ratio;
      const id = curr._id;
      const partyId = beneIn.dependentBenes[i].party._id;
      let childInfoParty = true;

      if (!(curr.party && curr.party.firstName)) {
        // sometimes it does not exist in current benes - so we have to get it from
        // relatedParty.relations
        const relationsIndex = BeneListService.relationIdToIndexMap[partyId];
        if (beneIn.relatedParty) {
          curr = beneIn.relatedParty.relations[relationsIndex].target;
        }
        childInfoParty = false;
      }

      const beneChild: BeneChild = {
        beneficiaryPartyType: 'person',
        action: 'modified',
        ratio: ratio,
        id: id,
        partyInfo: {
          person: {
            firstName: childInfoParty ? curr.party.firstName : curr.firstName,
            middleName: childInfoParty ? curr.party.middleName || '' : curr.middleName || '',
            lastName: childInfoParty ? curr.party.lastName : curr.lastName,
            dob: childInfoParty ? this.dateFormat(curr.party.dob) || '' : this.dateFormat(curr.dob) || '',
            govtId: childInfoParty ? curr.party.govtId || '' : curr.govtId || '',
            govtIdType: 'ssn',
          },
        },
      };
      if (beneIn.dependentBenes[i].contactInfo && beneIn.dependentBenes[i].contactInfo.residentialAddress) {
        beneChild.partyInfo.person.residentialAddress = {
          street: beneIn.dependentBenes[i].contactInfo.residentialAddress.street,
          line1: beneIn.dependentBenes[i].contactInfo.residentialAddress.line1
            ? beneIn.dependentBenes[i].contactInfo.residentialAddress.line1
            : '',
          line2: beneIn.dependentBenes[i].contactInfo.residentialAddress.line2
            ? beneIn.dependentBenes[i].contactInfo.residentialAddress.line2
            : '',
          city: beneIn.dependentBenes[i].contactInfo.residentialAddress.city,
          state: beneIn.dependentBenes[i].contactInfo.residentialAddress.state,
          zip: beneIn.dependentBenes[i].contactInfo.residentialAddress.zip,
        };
      }

      let phoneNumber = null;
      if (beneIn.dependentBenes[i].contactInfo) {
        phoneNumber = {
          number: beneIn.dependentBenes[i].contactInfo.phone ? beneIn.dependentBenes[i].contactInfo.phone.number : null,
        };
        beneChild.partyInfo.phone = phoneNumber;
        beneChild.partyInfo.email = beneIn.dependentBenes[i].contactInfo.email
          ? beneIn.dependentBenes[i].contactInfo.email
          : null;
      }
      toReturn.push(beneChild);
    }
    return toReturn;
  }

  getPrimaryBeneList(): Beneficiary[] {
    return BeneListService.primaryBeneList;
  }

  getContingentBeneList(): Beneficiary[] {
    return BeneListService.contingentBeneList;
  }

  getDeletedContingentList(): Beneficiary[] {
    return BeneListService.deletedContingentBeneList;
  }

  getDeletedPrimaryList(): Beneficiary[] {
    return BeneListService.deletedPrimaryBeneList;
  }

  setCurrentBene(bene: Beneficiary): void {
    BeneListService.currentBene = bene;
  }

  getCurrentBene(): Beneficiary {
    return BeneListService.currentBene;
  }

  getChanges(): boolean {
    return BeneListService.changes;
  }

  markChanged(): void {
    BeneListService.changes = true;
  }

  resetChanges(): void {
    BeneListService.changes = false;
    BeneListService.primaryChanged = false;
    BeneListService.contingentChanged = false;
    BeneListService.custodianChanged = false;
  }

  addBeneList(bene: Beneficiary, type: string): void {
    this.markChanged();
    if (type === 'Primary') {
      BeneListService.primaryChanged = true;
      BeneListService.primaryBeneList.push(bene);
      if (bene.beneficiaryType === '2' || bene.beneficiaryType === '6') {
        BeneListService.trustEstateCount++;
        this.removeContingentList();
      }
    } else {
      BeneListService.contingentChanged = true;
      BeneListService.contingentBeneList.push(bene);
    }
    this.splitPercent(type);
  }

  removeContingentList(): void {
    BeneListService.contingentChanged = true;
    const toDelete = BeneListService.contingentBeneList;
    for (let i = 0; i < toDelete.length; i++) {
      const removed = toDelete[i];
      removed.action = 'deleted';
      if (removed.id) {
        BeneListService.deletedContingentBeneList.push(removed);
      }
    }
    BeneListService.contingentBeneList = [];
  }

  deleteFromBeneList(bene: Beneficiary, type: string): void {
    this.markChanged();
    if (type === 'Primary') {
      BeneListService.primaryChanged = true;
      const beneIndex: number = BeneListService.primaryBeneList.indexOf(bene);
      if (bene.id) {
        BeneListService.deletedPrimaryBeneList.push(bene);
      }
      BeneListService.primaryBeneList.splice(beneIndex, 1);
      if (bene.beneficiaryType === '2' || bene.beneficiaryType === '6') {
        BeneListService.trustEstateCount--;
      }
    } else {
      BeneListService.contingentChanged = true;
      const beneIndex: number = BeneListService.contingentBeneList.indexOf(bene);
      if (bene.id) {
        BeneListService.deletedContingentBeneList.push(bene);
      }
      BeneListService.contingentBeneList.splice(beneIndex, 1);
    }
    this.splitPercent(type);
  }

  updateBene(newBene: Beneficiary, type: string): void {
    this.markChanged();
    if (type === 'Primary') {
      BeneListService.primaryChanged = true;
      const beneIndex: number = BeneListService.primaryBeneList.indexOf(BeneListService.currentBene);
      BeneListService.primaryBeneList[beneIndex] = newBene;
    } else {
      BeneListService.contingentChanged = true;
      const beneIndex: number = BeneListService.contingentBeneList.indexOf(BeneListService.currentBene);
      BeneListService.contingentBeneList[beneIndex] = newBene;
    }
  }

  reset(): void {
    BeneListService.contingentBeneList = [];
    BeneListService.primaryBeneList = [];
    BeneListService.deletedPrimaryBeneList = [];
    BeneListService.deletedContingentBeneList = [];
    BeneListService.custodian = null;
    BeneListService.trustEstateCount = 0;
    BeneListService.relationIdToIndexMap = {};
    this.resetChanges();
  }

  resetInit(): void {
    BeneListService.init = true;
  }

  hideContingent(): boolean {
    return BeneListService.trustEstateCount !== 0;
  }

  setCustodian(custodian): void {
    this.markChanged();
    BeneListService.custodianChanged = true;
    BeneListService.custodian = custodian;
  }

  getCustodian() {
    return BeneListService.custodian;
  }

  setPrimaryOrContingent(type: string): void {
    BeneListService.type = type;
  }

  getPrimaryOrContingent(): string {
    return BeneListService.type;
  }

  splitPercent(type: string): void {
    if (BeneListService.splitBene) {
      let list = BeneListService.primaryBeneList;
      if (type === 'Contingent') {
        list = BeneListService.contingentBeneList;
      }
      if (list.length === 0) {
        return;
      }

      const num = list.length;

      const percent = Math.floor((100 / num) * 100);
      const remainder = 10000 - Math.floor((100 / num) * 100) * num;

      for (let i = 0; i < num; i++) {
        let total = percent + (i < remainder ? 1 : 0);
        total = total / 100;
        const temp = list[i];
        this.setCurrentBene(temp);
        temp.ratio = total;
        this.updateBene(temp, type);
      }
    }
  }

  public getGovernmentId(): string {
    return BeneListService.governmentId;
  }

  public isPolicyAdminDown(): boolean {
    return this.policyAdminDown;
  }

  public needShowComplexOrTertiaryBeneMessage(): boolean {
    return this.showComplexOrTertiaryBeneMessage;
  }

  public getChannel(): {} {
    return this.channel;
  }

  public getChannelName(): string {
    return this.channelName;
  }

  private mapToBulkUpdateBeneficiary(beneList: Beneficiary[]): BulkUpdateBeneficiary[] {
    const bulkUpdateBeneficiaryList: BulkUpdateBeneficiary[] = [];
    for (const beneListItem of beneList) {
      const benePartyInfo = beneListItem.partyInfo;
      const benePartyInfoPerson = benePartyInfo ? (benePartyInfo as PartyPerson).person : null;
      const benePartyInfoEntity = benePartyInfo ? (benePartyInfo as Party).entity : null;
      const benePartyInfoAddress = benePartyInfoPerson
        ? benePartyInfoPerson.residentialAddress
        : benePartyInfoEntity
        ? benePartyInfoEntity.legalAddress
        : null;
      const beneAddress: Address = {
        street: benePartyInfoAddress ? benePartyInfoAddress.street : null,
        line1: benePartyInfoAddress ? benePartyInfoAddress.line1 : null,
        line2: benePartyInfoAddress ? benePartyInfoAddress.line2 : null,
        city: benePartyInfoAddress ? benePartyInfoAddress.city : null,
        state: benePartyInfoAddress ? benePartyInfoAddress.state : null,
        zip: benePartyInfoAddress ? benePartyInfoAddress.zip : null,
        country: benePartyInfoAddress ? benePartyInfoAddress.country : null,
      };

      const bulkUpdateDependentBeneList: BulkUpdateBeneficiary[] = [];

      if (beneListItem.dependentBenes && beneListItem.dependentBenes.length) {
        for (const dependentBene of beneListItem.dependentBenes) {
          const dependentPartyInfo = dependentBene.partyInfo;
          const dependentPartyInfoPerson = dependentPartyInfo.person;
          const dependentAddress = dependentPartyInfoPerson.residentialAddress;
          const dependentBeneAddress: Address = {
            street: dependentAddress.street,
            line1: dependentAddress.line1,
            line2: dependentAddress.line2,
            city: dependentAddress.city,
            state: dependentAddress.state,
            zip: dependentAddress.zip,
            country: dependentAddress.country,
          };
          const bulkUpdateDependentBene: BulkUpdateBeneficiary = {
            id: dependentBene.id,
            dob: dependentPartyInfoPerson.dob,
            email: dependentPartyInfo.email,
            name: dependentPartyInfo.name,
            phone: dependentPartyInfo.phone.number,
            ratio: dependentBene.ratio,
            type: '1',
            perStirpes: Utils.getPAPerstirpe(beneListItem.beneficiaryType),
            firstName: dependentPartyInfoPerson.firstName,
            middleName: dependentPartyInfoPerson.middleName,
            lastName: dependentPartyInfoPerson.lastName,
            gender: dependentPartyInfoPerson.gender,
            relationToInsured: dependentBene.beneficiaryPartyType,
            govtId: dependentPartyInfoPerson.govtId,
            govtIdType: dependentPartyInfoPerson.govtIdType,
            address: dependentBeneAddress,
          };
          bulkUpdateDependentBeneList.push(bulkUpdateDependentBene);
        }
      }

      const relatedPartyInfo = beneListItem.relatedParty ? beneListItem.relatedParty.partyInfo.person : null;
      const relationDescription = benePartyInfo ? benePartyInfo.relation : null;

      const bulkUpdateBeneficiary: BulkUpdateBeneficiary = {
        id: beneListItem.id,
        dob: benePartyInfoPerson ? benePartyInfoPerson.dob : null,
        email: benePartyInfo ? benePartyInfo.email : null,
        name: benePartyInfo ? benePartyInfo.name : null,
        phone: benePartyInfo && benePartyInfo.phone ? benePartyInfo.phone.number : null,
        ratio: beneListItem.ratio,
        type: Utils.getPABeneficiaryType(beneListItem.beneficiaryType),
        perStirpes: Utils.getPAPerstirpe(beneListItem.beneficiaryType),
        firstName: benePartyInfoPerson ? benePartyInfoPerson.firstName : null,
        middleName: benePartyInfoPerson ? benePartyInfoPerson.middleName : null,
        lastName: benePartyInfoPerson ? benePartyInfoPerson.lastName : null,
        gender: benePartyInfoPerson ? benePartyInfoPerson.gender : null,
        relationToInsured: relationDescription ? relationDescription.description : beneListItem.beneficiaryPartyType,
        dependentBeneficiaries: bulkUpdateDependentBeneList,
        govtId: benePartyInfoPerson
          ? benePartyInfoPerson.govtId
          : benePartyInfoEntity
          ? benePartyInfoEntity.govtId
          : null,
        govtIdType: benePartyInfoPerson
          ? benePartyInfoPerson.govtIdType
          : benePartyInfoEntity
          ? benePartyInfoEntity.govtIdType
          : null,
        trustType: benePartyInfoEntity ? benePartyInfoEntity.trustType : null,
        formedDate: benePartyInfoEntity ? benePartyInfoEntity.formedDate : null,
        spouseFirstName: relatedPartyInfo ? relatedPartyInfo.firstName : null,
        spouseMiddleName: relatedPartyInfo ? relatedPartyInfo.middleName : null,
        spouseLastName: relatedPartyInfo ? relatedPartyInfo.lastName : null,
        address: beneAddress,
      };
      bulkUpdateBeneficiaryList.push(bulkUpdateBeneficiary);
    }
    return bulkUpdateBeneficiaryList;
  }
}
